import axios from "axios";
import {
  url,
  cancelAppointment,
  checkCancellationPolicy,
  rescheduleAppointment,
} from "../crudRequests";
import {createBookingWizardUrl} from "../lib/bookingWizard";

// The dependent-scoped API.
//
// Every call names the dependent in the PATH. There is no client-side "current
// profile" and no server-side one either: the previous portal held whose record
// it was showing in a reducer and re-fetched everything with that pid, so the
// browser decided what to load and the server only vetoed. A health app that
// can be out of sync about whose data is on screen has a dangerous class of bug
// by construction.

function authHeaders(state) {
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + state.jwt,
      "X-Tenant": "patient",
    },
    withCredentials: true,
  };
}

function scoped(dependentPatientId, path = "") {
  return `${url}/patient-app/dependents/${encodeURIComponent(dependentPatientId)}${path}`;
}

/**
 * Who this account may act for, and how far.
 *
 * The server decides which sections exist for each dependent; the client
 * renders what it is given. A section the guardian has no access to is absent
 * from the response, not hidden in the browser — hiding in the browser means
 * the data was still sent.
 */
export async function fetchDependents(state) {
  const response = await axios.get(`${url}/patient-app/dependents`, authHeaders(state));
  return response.data?.dependents || [];
}

/**
 * The guardian's home screen for one dependent, composed server-side.
 *
 * One request rather than five assembled here: a composition is where
 * individually harmless fragments add up to a disclosure, and that decision
 * belongs on the side of the wire that holds the policy.
 */
export async function fetchDependentOverview(state, dependentPatientId) {
  const response = await axios.get(scoped(dependentPatientId, "/overview"), authHeaders(state));
  return response.data;
}

export async function fetchDependentProfile(state, dependentPatientId) {
  const response = await axios.get(scoped(dependentPatientId, "/profile"), authHeaders(state));
  return response.data;
}

export async function updateDependentProfile(state, dependentPatientId, patch) {
  const response = await axios.patch(
    scoped(dependentPatientId, "/profile"),
    patch,
    authHeaders(state)
  );
  return response.data;
}

export async function fetchDependentAppointments(state, dependentPatientId) {
  const response = await axios.get(scoped(dependentPatientId, "/appointments"), authHeaders(state));
  return response.data;
}

export async function fetchDependentForms(state, dependentPatientId) {
  const response = await axios.get(scoped(dependentPatientId, "/forms"), authHeaders(state));
  return response.data;
}

export async function fetchDependentBilling(state, dependentPatientId, params = {}) {
  const query = new URLSearchParams(params).toString();
  const response = await axios.get(
    scoped(dependentPatientId, `/billing${query ? `?${query}` : ""}`),
    authHeaders(state)
  );
  return response.data;
}

/**
 * The dependent's memberships and packages — the same projection the patient
 * reads of their own plans, behind the billing `view` grade (audit C48).
 */
export async function fetchDependentPlans(state, dependentPatientId) {
  const response = await axios.get(scoped(dependentPatientId, "/billing/plans"), authHeaders(state));
  return response.data;
}

export async function fetchDependentDocuments(state, dependentPatientId) {
  const response = await axios.get(scoped(dependentPatientId, "/documents"), authHeaders(state));
  return response.data;
}

/**
 * Start a booking for a dependent.
 *
 * A DELEGATION to the canonical booking flow, not a second booking flow: the
 * same wizard, the same availability engine, the same fee and notification
 * rules, with the dependent as the subject. The site offers them only if the
 * guardian may book for them, and every booking door asks the guardianship
 * policy again, so this is a request, never a claim.
 */
export async function startDependentBooking(state, dependentPatientId) {
  return {bookingUrl: createBookingWizardUrl(state, {patientId: dependentPatientId})};
}

// ---------------------------------------------------------------------------
// Acting for a dependent
// ---------------------------------------------------------------------------
//
// Cancelling and rescheduling do NOT have a dependent-scoped URL, and that is
// the design rather than an omission. The appointment already names whose it
// is, so the server derives the subject from the record instead of taking it
// from the client at all — which is stronger than a path parameter, not weaker.
//
// The consequence is that these three calls are the SAME functions the
// patient's own screens use, unchanged. There is one cancellation window, one
// fee, one notification fan-out and one answer to "may I?", and a second URL
// for the same act would eventually give a different one.

export async function fetchDependentCancellationPolicy(state, appointmentId) {
  const response = await checkCancellationPolicy(state, appointmentId);
  return response.data;
}

export async function cancelDependentAppointment(state, appointmentId, reason) {
  const response = await cancelAppointment(state, appointmentId, reason);
  return response.data;
}

/**
 * Where this appointment could move to.
 *
 * The appointment it must ignore is the one in the path, so the exclusion is
 * derived rather than sent — a client naming the appointment to exclude is a
 * client the server would have to trust about which one is its own.
 */
export async function fetchDependentRescheduleOptions(state, appointmentId, {from, to} = {}) {
  const query = new URLSearchParams({
    db: state.db,
    ...(from ? {from: String(from)} : {}),
    ...(to ? {to: String(to)} : {}),
  }).toString();
  const response = await axios.get(
    `${url}/appointments/${encodeURIComponent(appointmentId)}/reschedule-options?${query}`,
    authHeaders(state)
  );
  return response.data;
}

export async function rescheduleDependentAppointment(state, appointmentId, slot) {
  // The server decides the status, the confirmation and the notification. The
  // client sends when it should start and how long it runs, and nothing else.
  const response = await rescheduleAppointment(state, appointmentId, {
    startAt: slot.start,
    endAt: slot.end,
    ...(slot.did ? {did: slot.did} : {}),
  });
  return response.data;
}

/** One form in full, so a guardian can fill it in. */
export async function fetchDependentForm(state, dependentPatientId, formSubmissionId) {
  const response = await axios.get(
    scoped(dependentPatientId, `/forms/${encodeURIComponent(formSubmissionId)}`),
    authHeaders(state)
  );
  return response.data;
}

/**
 * Pay the dependent's charges with the guardian's own card.
 *
 * The dependent is in the PATH and the payer is the session — the two are
 * different people here, which is exactly why this call cannot be the patient's
 * own `/patient-app/billing/collect`: that one derives both from the session.
 */
export async function payDependentBilling(state, dependentPatientId, payload) {
  const response = await axios.post(
    scoped(dependentPatientId, "/billing/collect"),
    payload,
    authHeaders(state)
  );
  return response.data;
}

export async function fetchDependentCoverage(state, dependentPatientId) {
  const response = await axios.get(
    scoped(dependentPatientId, "/billing/coverage"),
    authHeaders(state)
  );
  return response.data;
}

export async function updateDependentCoverage(state, dependentPatientId, coverage) {
  const response = await axios.put(
    scoped(dependentPatientId, "/billing/coverage"),
    {coverage},
    authHeaders(state)
  );
  return response.data;
}

/** A short-lived signed URL for one document the guardian may open. */
export async function fetchDependentDocumentUrl(state, dependentPatientId, fileId) {
  const response = await axios.get(
    scoped(dependentPatientId, `/documents/url?fileId=${encodeURIComponent(fileId)}`),
    authHeaders(state)
  );
  return response.data?.downloadUrl || null;
}

/**
 * Upload a document onto the dependent's record.
 *
 * `/crudStorageUpload` is the one endpoint that writes to S3 — the file name
 * rules, the traversal check, the provenance row and the clinic's notification
 * all live there. It asks the guardianship policy for `documents: upload` when
 * the folder is not the caller's own, which is why this is a plain call to it
 * with a different folder rather than a second upload route.
 */
export async function uploadDependentDocument(state, dependentPatientId, file) {
  const body = new FormData();
  body.append("file", file);
  const query = new URLSearchParams({
    db: state.db,
    directoryName: `${dependentPatientId}/Client Uploads/`,
    s3BucketName: state.organization?.s3BucketName || "",
  }).toString();

  const response = await axios.post(`${url}/crudStorageUpload?${query}`, body, {
    headers: {
      Authorization: "Bearer " + state.jwt,
      "X-Tenant": "patient",
    },
    withCredentials: true,
  });
  return response.data;
}

/** The server writes for people; show its sentence rather than a generic one. */
export function messageFor(error, fallback) {
  return (
    error?.response?.data?.error?.message ||
    // The canonical appointment routes answer in their own older shape.
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}
